import { useState, useEffect } from "react";
import { Modal, Form, Button, Alert, Spinner, Row, Col } from "react-bootstrap";
import { IconRocket, IconCheck } from "@tabler/icons-react";
import Select from "react-select";
import NumberFormat from "react-number-format";
import Swal from "sweetalert2";
import {
  getAvailableAgents,
  createAgentDeployment,
  getAvailableAgentFlows,
  createAgentFlowDeployment,
} from "../../AgentDeployments/services/AgentDeploymentServices";
import SubscriptionPaymentModal from "./SubscriptionPaymentModal";

const AgentImplementationModal = ({ show, onHide, subaccount, onSuccess }) => {
  // Estados do formulário
  const [formData, setFormData] = useState({
    agent_version: "1.0",
    agent: null,
    setup_fee: "",
    monthly_fee: "",
    support_fee: "",
    support_type: "basic",
    support_duration_months: 12,
    notes: "",
  });

  // Estados de controle
  const [loading, setLoading] = useState(false);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [availableAgents, setAvailableAgents] = useState([]);
  const [errors, setErrors] = useState({});
  
  // Estados do modal de pagamento
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  
  // Estado para salvar dados de implementação quando não há pagamento
  const [pendingImplementationData, setPendingImplementationData] = useState(null);

  useEffect(() => {
    if (show) {
      // Reset form quando modal abrir
      setFormData({
        agent_version: "1.0",
        agent: null,
        setup_fee: "",
        monthly_fee: "",
        support_fee: "",
        support_type: "basic",
        support_duration_months: 12,
        notes: "",
      });
      setErrors({});
      setPendingImplementationData(null);
      loadAvailableAgents("1.0");
    }
  }, [show]);

  // Recarregar lista de agentes quando a versão mudar
  useEffect(() => {
    if (show) {
      loadAvailableAgents(formData.agent_version);
      setFormData((prev) => ({ ...prev, agent: null }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.agent_version]);

  const loadAvailableAgents = async (version = "1.0") => {
    try {
      setAgentsLoading(true);

      if (version === "2.0") {
        const response = await getAvailableAgentFlows();
        const list = response?.data?.agent_flows;
        if (!Array.isArray(list)) {
          setAvailableAgents([]);
          return;
        }
        const options = list.map((flow) => ({
          value: flow.id,
          label: flow.name,
          agentData: {
            id: flow.id,
            agent_name: flow.name,
            agent_job: flow.description || "AgentFlow v2",
            is_active: flow.is_active,
            is_published: flow.is_published,
          },
        }));
        setAvailableAgents(options);
        return;
      }

      const response = await getAvailableAgents();
      if (
        !response.data ||
        !response.data.agents ||
        !Array.isArray(response.data.agents)
      ) {
        setAvailableAgents([]);
        return;
      }

      const agentOptions = response.data.agents.map((agent) => ({
        value: agent.id,
        label: `${agent.agent_name} (${
          agent.agent_job || "Área não especificada"
        })`,
        agentData: agent,
      }));
      setAvailableAgents(agentOptions);
    } catch (error) {
      console.error("Erro ao carregar agentes:", error);
      setAvailableAgents([]);
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Não foi possível carregar os agentes disponíveis.",
        confirmButtonColor: "#dc3545",
      });
    } finally {
      setAgentsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Limpar erro específico do campo
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.agent) {
      newErrors.agent = "Selecione um agente";
    }

    if (
      !formData.setup_fee ||
      parseFloat(formData.setup_fee.replace(/[^\d,]/g, "").replace(",", ".")) <=
        0
    ) {
      newErrors.setup_fee =
        "Taxa de setup é obrigatória e deve ser maior que zero";
    }

    if (
      !formData.monthly_fee ||
      parseFloat(
        formData.monthly_fee.replace(/[^\d,]/g, "").replace(",", ".")
      ) <= 0
    ) {
      newErrors.monthly_fee =
        "Mensalidade é obrigatória e deve ser maior que zero";
    }

    if (
      formData.support_fee &&
      parseFloat(
        formData.support_fee.replace(/[^\d,]/g, "").replace(",", ".")
      ) < 0
    ) {
      newErrors.support_fee = "Taxa de suporte não pode ser negativa";
    }

    if (
      !formData.support_duration_months ||
      formData.support_duration_months < 1
    ) {
      newErrors.support_duration_months =
        "Duração do suporte deve ser pelo menos 1 mês";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      const isV2 = formData.agent_version === "2.0";

      // Preparar payload (chave de agente muda conforme versão)
      const basePayload = {
        subaccount: subaccount.id,
        setup_fee: formData.setup_fee.replace(/[^\d,]/g, "").replace(",", "."),
        monthly_fee: formData.monthly_fee
          .replace(/[^\d,]/g, "")
          .replace(",", "."),
        support_fee: formData.support_fee
          ? formData.support_fee.replace(/[^\d,]/g, "").replace(",", ".")
          : 0,
        support_type: formData.support_type,
        support_duration_months: parseInt(formData.support_duration_months),
        notes: formData.notes.trim(),
      };

      const payload = isV2
        ? { ...basePayload, agent_flow: formData.agent.value }
        : { ...basePayload, agent: formData.agent.value };

      const response = isV2
        ? await createAgentFlowDeployment(payload)
        : await createAgentDeployment(payload);

      // Sucesso (201)
      if (response.status === 201 && response.data) {
        const data = response.data;
        const skippedGoogle = data?.skipped?.google_integrations || [];

        const GOOGLE_LABELS = {
          google_workspace: "Google Workspace",
          google_sheets: "Google Sheets",
          google_calendar: "Google Calendar",
          google_drive: "Google Drive",
          google_docs: "Google Docs",
          gmail: "Gmail",
        };
        const googleListHtml = skippedGoogle.length
          ? `
            <hr>
            <div style="background: #fff8e1; border-left: 4px solid #f59e0b; padding: 10px 12px; border-radius: 4px;">
              <p style="margin: 0 0 6px 0; font-weight: 600; color: #92400e;">
                <i class="fas fa-exclamation-triangle"></i> Reconexão necessária
              </p>
              <p style="margin: 0 0 6px 0; font-size: 13px; color: #78350f;">
                As integrações Google abaixo <strong>não foram copiadas</strong> e precisam ser reconectadas pelo cliente na subconta:
              </p>
              <ul style="margin: 0; padding-left: 20px; font-size: 13px; color: #78350f;">
                ${skippedGoogle
                  .map((t) => `<li>${GOOGLE_LABELS[t] || t}</li>`)
                  .join("")}
              </ul>
            </div>
          `
          : "";

        Swal.fire({
          icon: "success",
          title: "🎉 Implementação Criada!",
          html: `
            <div style="text-align: left; padding: 10px;">
              <p><strong>Agente:</strong> ${
                formData.agent.agentData.agent_name
              }</p>
              <p><strong>Cliente:</strong> ${subaccount.company_name}</p>
              ${
                data.agent_copied
                  ? '<p class="text-success"><i class="fas fa-check"></i> Agente implementado com sucesso</p>'
                  : ""
              }

              <hr>
              <p class="text-muted">${data.message}</p>
              ${googleListHtml}
            </div>
          `,
          confirmButtonText: "Continuar",
          confirmButtonColor: "#28a745",
        });

        if (onSuccess) onSuccess();
        onHide();
        return;
      }
    } catch (error) {
      console.error("Erro ao criar implementação:", error);

      // Verificar status HTTP e dados da resposta
      const status = error.response?.status;
      const errorData = error.response?.data;

      // Verificar se é erro de assinatura necessária
      if (
        errorData &&
        errorData.error === "Nenhuma assinatura disponível" &&
        errorData.requires_payment
      ) {
        Swal.fire({
          icon: "warning",
          title: "💳 Assinatura Necessária",
          html: `
            <div style="text-align: left; padding: 15px;">
              <p><strong>Situação:</strong> ${errorData.error}</p>
              <div class="alert alert-info" style="margin: 10px 0;">
                <h6><strong>📋 Informação:</strong></h6>
                <p>${errorData.message}</p>
              </div>
              <hr>
              <div class="alert alert-warning" style="margin: 10px 0; background: #fff3cd; border: 1px solid #ffeaa7; border-radius: 8px; padding: 12px;">
                <h6><strong>⚠️ Ação Necessária:</strong></h6>
                <p>Para implementar este agente, é necessário ter uma assinatura ativa disponível.</p>
              </div>
            </div>
          `,
          showCancelButton: true,
          confirmButtonText: "💳 Contratar Assinatura",
          cancelButtonText: "Cancelar",
          confirmButtonColor: "#007bff",
          cancelButtonColor: "#6c757d",
        }).then((result) => {
          if (result.isConfirmed) {
            // Salvar dados de implementação para usar após o pagamento
            const isV2Pending = formData.agent_version === "2.0";
            const implementationData = {
              _isV2: isV2Pending,
              ...(isV2Pending
                ? { agent_flow: formData.agent.value }
                : { agent: formData.agent.value }),
              subaccount: subaccount.id,
              setup_fee: formData.setup_fee
                .replace(/[^\d,]/g, "")
                .replace(",", "."),
              monthly_fee: formData.monthly_fee
                .replace(/[^\d,]/g, "")
                .replace(",", "."),
              support_fee: formData.support_fee
                ? formData.support_fee.replace(/[^\d,]/g, "").replace(",", ".")
                : 0,
              support_type: formData.support_type,
              support_duration_months: parseInt(
                formData.support_duration_months
              ),
              notes: formData.notes.trim(),
            };

            setPendingImplementationData(implementationData);

            // Abrir modal de pagamento de assinatura
            const paymentInfo = {
              value: 0, // Valor será definido no modal de assinatura
              description: `Assinatura para implementação de agente`,
              agentName: formData.agent?.agentData?.agent_name,
              clientName: subaccount?.company_name,
            };

            setPaymentData(paymentInfo);
            setShowPaymentModal(true);
          }
        });
        return;
      }

      if (status === 402 && errorData) {
        // Limite Excedido (402)
        Swal.fire({
          icon: "warning",
          title: "🚨 Limite de Agentes Excedido",
          html: `
            <div style="text-align: left; padding: 15px;">
              <p><strong>Situação:</strong> ${errorData.error}</p>
              <p><strong>Uso atual:</strong> ${errorData.current_usage_after}/${
            errorData.total_limit
          } agentes</p>
              <hr>
              ${
                errorData.requires_payment
                  ? `
                <div class="alert alert-info" style="margin: 10px 0;">
                  <h6><strong>💳 Solução:</strong></h6>
                  <p>Para continuar implementando agentes, é necessário o pagamento de <strong>R$ ${errorData.extra_value_per_additional?.toFixed(
                    2
                  )}</strong> por agente adicional.</p>
                </div>
              `
                  : ""
              }
              <p class="text-muted">${errorData.message}</p>
            </div>
          `,
          showCancelButton: true,
          confirmButtonText: errorData.requires_payment
            ? "💳 Contratar agora"
            : "Entendi",
          cancelButtonText: "Cancelar",
          confirmButtonColor: errorData.requires_payment
            ? "#007bff"
            : "#6c757d",
          cancelButtonColor: "#6c757d",
        }).then((result) => {
          if (result.isConfirmed && errorData.requires_payment) {
            // Salvar dados de implementação para usar após o pagamento
            const isV2Pending = formData.agent_version === "2.0";
            const implementationData = {
              _isV2: isV2Pending,
              ...(isV2Pending
                ? { agent_flow: formData.agent.value }
                : { agent: formData.agent.value }),
              subaccount: subaccount.id,
              setup_fee: formData.setup_fee
                .replace(/[^\d,]/g, "")
                .replace(",", "."),
              monthly_fee: formData.monthly_fee
                .replace(/[^\d,]/g, "")
                .replace(",", "."),
              support_fee: formData.support_fee
                ? formData.support_fee.replace(/[^\d,]/g, "").replace(",", ".")
                : 0,
              support_type: formData.support_type,
              support_duration_months: parseInt(
                formData.support_duration_months
              ),
              notes: formData.notes.trim(),
            };

            setPendingImplementationData(implementationData);

            // Abrir modal de pagamento
            const paymentInfo = {
              value: errorData.extra_value_per_additional,
              description: `Assinatura agente adicional`,
              agentName: formData.agent?.agentData?.agent_name,
              clientName: subaccount?.company_name,
            };

            setPaymentData(paymentInfo);
            setShowPaymentModal(true);
          } else if (result.isConfirmed && !errorData.requires_payment) {
            // Caso não precise de pagamento, salvar dados para aguardar
            const isV2Pending = formData.agent_version === "2.0";
            const implementationData = {
              _isV2: isV2Pending,
              ...(isV2Pending
                ? { agent_flow: formData.agent.value }
                : { agent: formData.agent.value }),
              subaccount: subaccount.id,
              setup_fee: formData.setup_fee
                .replace(/[^\d,]/g, "")
                .replace(",", "."),
              monthly_fee: formData.monthly_fee
                .replace(/[^\d,]/g, "")
                .replace(",", "."),
              support_fee: formData.support_fee
                ? formData.support_fee.replace(/[^\d,]/g, "").replace(",", ".")
                : 0,
              support_type: formData.support_type,
              support_duration_months: parseInt(
                formData.support_duration_months
              ),
              notes: formData.notes.trim(),
            };

            setPendingImplementationData(implementationData);

            Swal.fire({
              icon: "info",
              title: "Implementação Agendada",
              html: `
                <div style="text-align: left; padding: 10px;">
                  <p><strong>Agente:</strong> ${formData.agent.agentData.agent_name}</p>
                  <p><strong>Cliente:</strong> ${subaccount.company_name}</p>
                  <hr>
                  <p class="text-muted">As informações foram salvas. Aguarde enquanto processamos a implementação.</p>
                </div>
              `,
              confirmButtonText: "Entendi",
              confirmButtonColor: "#28a745",
            });
          }
        });
        return;
      }

      if (status === 403 && errorData) {
        // Feature Desabilitada (403)
        Swal.fire({
          icon: "error",
          title: "🚫 Feature Desabilitada",
          html: `
            <div style="text-align: left; padding: 15px;">
              <p><strong>Motivo:</strong> ${errorData.error}</p>
              <div class="alert alert-warning" style="margin: 10px 0;">
                <h6><strong>⚠️ Atenção:</strong></h6>
                <p>A funcionalidade "Criar Agente" está desabilitada em sua conta.</p>
                <p><strong>Uso atual:</strong> ${errorData.current_usage_after}/${errorData.total_limit} agentes</p>
              </div>
              <hr>
              <p class="text-muted">Entre em contato com o suporte para habilitar esta funcionalidade.</p>
            </div>
          `,
          confirmButtonText: "Entrar em Contato",
          showCancelButton: true,
          cancelButtonText: "Fechar",
          confirmButtonColor: "#007bff",
          cancelButtonColor: "#6c757d",
        }).then((result) => {
          if (result.isConfirmed) {
            // Redirecionar para suporte ou abrir modal de contato
            Swal.fire({
              icon: "info",
              title: "Contatar Suporte",
              text: "Você será redirecionado para a página de suporte.",
              confirmButtonColor: "#007bff",
            });
            // Aqui você pode implementar a navegação para o suporte
          }
        });
        return;
      }

      if (status === 409 && errorData) {
        // Agente Já Implementado (409)
        const existing = errorData.existing_deployment;
        const deployedDate = existing?.deployed_at
          ? new Date(existing.deployed_at).toLocaleDateString("pt-BR")
          : "Data não disponível";

        Swal.fire({
          icon: "warning",
          title: "🔄 Agente Já Implementado",
          html: `
            <div style="text-align: left; padding: 15px;">
              <div class="alert alert-warning" style="margin: 10px 0; background: #fff3cd; border: 1px solid #ffeaa7; border-radius: 8px; padding: 12px;">
                <h6><strong>⚠️ Duplicação Detectada:</strong></h6>
                <p>${errorData.error}</p>
              </div>
              
              <div style="background: #f8f9fa; border-radius: 8px; padding: 15px; margin: 15px 0;">
                <h6><strong>📄 Implementação Existente:</strong></h6>
                <div style="margin: 8px 0;">
                  <p><strong>Agente:</strong> ${
                    existing?.agent_name || "Não especificado"
                  }</p>
                  <p><strong>Cliente:</strong> ${
                    existing?.subaccount_name || "Não especificado"
                  }</p>
                  <p><strong>Implementado em:</strong> ${deployedDate}</p>
                  <p><strong>ID da Implementação:</strong> #${
                    existing?.id || "N/A"
                  }</p>
                </div>
              </div>
              
              <hr>
              <div class="alert alert-info" style="margin: 10px 0; background: #d1ecf1; border: 1px solid #bee5eb; border-radius: 8px; padding: 12px;">
                <h6><strong>💡 O que fazer:</strong></h6>
                <ul style="margin: 5px 0; padding-left: 20px;">
                  <li>Verifique o status da implementação existente</li>
                  <li>Se necessário, gerencie a implementação atual antes de criar uma nova</li>
                  <li>Considere usar um agente diferente para este cliente</li>
                </ul>
              </div>
            </div>
          `,
          showCancelButton: true,
          confirmButtonText: "Entendi!",
          cancelButtonText: "Fechar",
          confirmButtonColor: "#007bff",
          cancelButtonColor: "#6c757d",
          width: "600px",
        }).then((result) => {
          // if (result.isConfirmed) {
          //   // Aqui você pode navegar para a página de detalhes da implementação existente
          //   // ou abrir um modal com os detalhes
          //   Swal.fire({
          //     icon: "info",
          //     title: "Navegar para Implementação",
          //     html: `
          //       <div style="text-align: center; padding: 10px;">
          //         <p>Redirecionando para os detalhes da implementação existente...</p>
          //         <p><strong>ID:</strong> #${existing?.id}</p>
          //       </div>
          //     `,
          //     confirmButtonColor: "#007bff",
          //     timer: 2000,
          //     showConfirmButton: false,
          //   });
          //   // Implementar navegação ou ação desejada aqui
          //   // Exemplo: window.location.href = `/implementacoes/${existing.id}`;
          // }
        });
        return;
      }

      if(errorData.non_field_errors){
        // Erro genérico sem campo específico
        Swal.fire({
          icon: "warning",
          title: "Oops!",
          text: errorData.non_field_errors[0],
          confirmButtonColor: "#dc3545",
        });
        return;
      }

      // Outros erros genéricos
      let errorMessage = "Não foi possível criar a implementação.";

      if (errorData) {
        if (typeof errorData === "string") {
          errorMessage = errorData;
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
      }

      Swal.fire({
        icon: "error",
        title: "Erro ao Criar Implementação",
        html: `
          <div style="text-align: left; padding: 10px;">
            <p><strong>Erro:</strong> ${errorMessage}</p>
            <hr>
            <p class="text-muted">Tente novamente ou entre em contato com o suporte se o problema persistir.</p>
          </div>
        `,
        confirmButtonColor: "#dc3545",
      });
    } finally {
      setLoading(false);
    }
  };

  // Handlers do modal de pagamento
  const handlePaymentSuccess = async (subscriptionResult) => {
    console.log("Pagamento realizado com sucesso:", subscriptionResult);
    
    Swal.fire({
      icon: "success",
      title: "Pagamento Aprovado!",
      html: `
        <div style="text-align: left; padding: 10px;">
          <p><strong>Valor:</strong> ${subscriptionResult.formattedValue}</p>
          <p><strong>Próximo vencimento:</strong> ${subscriptionResult.formattedNextDueDate}</p>
          <hr>
          <p class="text-muted">Aguarde enquanto fazemos a implementação do agente...</p>
        </div>
      `,
      showConfirmButton: false,
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    // Aguardar 2 segundos antes de implementar o agente
    await new Promise(resolve => setTimeout(resolve, 2000));

    // Fechar modal de pagamento
    setShowPaymentModal(false);
    setPaymentData(null);
    
    // Implementar o agente com os dados salvos
    if (pendingImplementationData) {
      try {
        const { _isV2, ...payload } = pendingImplementationData;
        const response = _isV2
          ? await createAgentFlowDeployment(payload)
          : await createAgentDeployment(payload);
        
        // Sucesso (201)
        if (response.status === 201 && response.data) {
          const data = response.data;
          
          Swal.fire({
            icon: "success",
            title: "🎉 Implementação Concluída!",
            html: `
              <div style="text-align: left; padding: 10px;">
                <div class="alert alert-success" style="margin: 10px 0;">
                  <h6><strong>✅ Pagamento e Implementação Realizados</strong></h6>
                </div>
                <p><strong>Agente:</strong> ${
                  formData.agent?.agentData?.agent_name
                }</p>
                <p><strong>Cliente:</strong> ${subaccount.company_name}</p>
                ${
                  data.agent_copied
                    ? '<p class="text-success"><i class="fas fa-check"></i> Agente implementado com sucesso</p>'
                    : ""
                }
                ${
                  data.usage_incremented
                    ? `<p><strong>Uso atual:</strong> ${data.current_usage_after}/${data.total_limit} agentes</p>`
                    : ""
                }
                <hr>
                <p class="text-muted">${data.message}</p>
              </div>
            `,
            confirmButtonText: "Continuar",
            confirmButtonColor: "#28a745",
          });

          // Limpar dados salvos
          setPendingImplementationData(null);
          
          if (onSuccess) onSuccess();
          onHide();
          return;
        }
      } catch (error) {
        console.error("Erro ao implementar agente após pagamento:", error);
        
        Swal.fire({
          icon: "error",
          title: "Erro na Implementação",
          html: `
            <div style="text-align: left; padding: 10px;">
              <div class="alert alert-warning" style="margin: 10px 0;">
                <h6><strong>⚠️ Pagamento Aprovado, mas Falha na Implementação</strong></h6>
              </div>
              <p>O pagamento foi processado com sucesso, mas ocorreu um erro ao implementar o agente.</p>
              <hr>
              <p class="text-muted">Entre em contato com o suporte para resolver esta situação.</p>
            </div>
          `,
          confirmButtonColor: "#dc3545",
        });
      }
    } else {
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Dados de implementação não encontrados. Tente novamente.",
        confirmButtonColor: "#dc3545",
      });
    }
  };

  const handlePaymentCancel = () => {
    setShowPaymentModal(false);
    setPaymentData(null);
    setPendingImplementationData(null);
  };

  const customSelectStyles = {
    control: (provided) => ({
      ...provided,
      borderColor: errors.agent ? "#dc3545" : provided.borderColor,
      "&:hover": {
        borderColor: errors.agent ? "#dc3545" : provided.borderColor,
      },
      minHeight: "38px", // Altura mínima adequada
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999, // Z-index muito alto para sobrepor o modal
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999, // Z-index para o portal
    }),
    // Garantir que o menu não seja cortado
    menuList: (provided) => ({
      ...provided,
      maxHeight: "200px", // Limitar altura do menu
    }),
  };

  // Verificar se formulário está válido para habilitar botão
  const isFormValid =
    formData.agent &&
    formData.setup_fee &&
    formData.monthly_fee &&
    formData.support_duration_months > 0;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center">
          <IconRocket size={24} className="me-2" />
          Nova Implementação de Agente
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {/* Informação da Subconta */}
          <Alert variant="info" className="mb-4">
            <strong>Cliente Selecionado:</strong>{" "}
            {subaccount?.company_name || "—"}
            {subaccount?.brand_name && (
              <span className="ms-2 text-muted">({subaccount.brand_name})</span>
            )}
          </Alert>

          <Row>
            {/* Versão do Agente */}
            <Col xs={12} className="mb-3">
              <Form.Label>
                <strong>Versão do Agente *</strong>
              </Form.Label>
              <Form.Select
                value={formData.agent_version}
                onChange={(e) =>
                  handleInputChange("agent_version", e.target.value)
                }
              >
                <option value="1.0">Agente 1.0 (Legado)</option>
                <option value="2.0">Agente 2.0 (AgentFlow)</option>
              </Form.Select>
              <Form.Text className="text-muted">
                {formData.agent_version === "2.0"
                  ? "Novo formato com fluxo visual, capacidades modulares e integrações MCP."
                  : "Formato legado de agentes."}
              </Form.Text>
            </Col>

            {/* Seleção do Agente */}
            <Col xs={12} className="mb-3">
              <Form.Label>
                <strong>
                  {formData.agent_version === "2.0" ? "AgentFlow" : "Agente"} *
                </strong>
              </Form.Label>
              {agentsLoading ? (
                <div className="d-flex align-items-center p-3 border rounded">
                  <Spinner size="sm" className="me-2" />
                  <span>Carregando agentes disponíveis...</span>
                </div>
              ) : (
                <Select
                  value={formData.agent}
                  onChange={(selectedOption) =>
                    handleInputChange("agent", selectedOption)
                  }
                  options={availableAgents}
                  placeholder="Selecione um agente..."
                  noOptionsMessage={() => "Nenhum agente disponível"}
                  isSearchable
                  styles={customSelectStyles}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              )}
              {errors.agent && (
                <div className="invalid-feedback d-block">{errors.agent}</div>
              )}
            </Col>

            {/* Taxa Setup */}
            <Col md={4} className="mb-3">
              <Form.Label>
                <strong>Taxa Setup *</strong>
              </Form.Label>
              <NumberFormat
                value={formData.setup_fee}
                onValueChange={(values) =>
                  handleInputChange("setup_fee", values.formattedValue)
                }
                thousandSeparator="."
                decimalSeparator=","
                prefix="R$ "
                decimalScale={2}
                fixedDecimalScale
                allowNegative={false}
                className={`form-control ${
                  errors.setup_fee ? "is-invalid" : ""
                }`}
                placeholder="R$ 0,00"
              />
              {errors.setup_fee && (
                <div className="invalid-feedback">{errors.setup_fee}</div>
              )}
            </Col>

            {/* Mensalidade */}
            <Col md={4} className="mb-3">
              <Form.Label>
                <strong>Mensalidade *</strong>
              </Form.Label>
              <NumberFormat
                value={formData.monthly_fee}
                onValueChange={(values) =>
                  handleInputChange("monthly_fee", values.formattedValue)
                }
                thousandSeparator="."
                decimalSeparator=","
                prefix="R$ "
                decimalScale={2}
                fixedDecimalScale
                allowNegative={false}
                className={`form-control ${
                  errors.monthly_fee ? "is-invalid" : ""
                }`}
                placeholder="R$ 0,00"
              />
              {errors.monthly_fee && (
                <div className="invalid-feedback">{errors.monthly_fee}</div>
              )}
            </Col>

            {/* Taxa de Suporte */}
            <Col md={4} className="mb-3">
              <Form.Label>
                <strong>Taxa de Suporte</strong>
              </Form.Label>
              <NumberFormat
                value={formData.support_fee}
                onValueChange={(values) =>
                  handleInputChange("support_fee", values.formattedValue)
                }
                thousandSeparator="."
                decimalSeparator=","
                prefix="R$ "
                decimalScale={2}
                fixedDecimalScale
                allowNegative={false}
                className={`form-control ${
                  errors.support_fee ? "is-invalid" : ""
                }`}
                placeholder="R$ 0,00"
              />
              {errors.support_fee && (
                <div className="invalid-feedback">{errors.support_fee}</div>
              )}
            </Col>

            {/* Tipo de Suporte */}
            <Col md={6} className="mb-3">
              <Form.Label>
                <strong>Tipo de Suporte</strong>
              </Form.Label>
              <Form.Select
                value={formData.support_type}
                onChange={(e) =>
                  handleInputChange("support_type", e.target.value)
                }
              >
                <option value="basic">Básico</option>
                <option value="premium">Premium</option>
              </Form.Select>
            </Col>

            {/* Duração do Suporte */}
            <Col md={6} className="mb-3">
              <Form.Label>
                <strong>Duração do Suporte (meses)</strong>
              </Form.Label>
              <Form.Control
                type="number"
                min="1"
                max="60"
                value={formData.support_duration_months}
                onChange={(e) =>
                  handleInputChange(
                    "support_duration_months",
                    parseInt(e.target.value) || 1
                  )
                }
                className={errors.support_duration_months ? "is-invalid" : ""}
              />
              {errors.support_duration_months && (
                <div className="invalid-feedback">
                  {errors.support_duration_months}
                </div>
              )}
            </Col>

            {/* Observações */}
            <Col xs={12} className="mb-3">
              <Form.Label>
                <strong>Observações</strong>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.notes}
                onChange={(e) => handleInputChange("notes", e.target.value)}
                placeholder="Observações adicionais sobre a implementação..."
                maxLength={500}
              />
              <Form.Text className="text-muted">
                {formData.notes.length}/500 caracteres
              </Form.Text>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={loading}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={loading || !isFormValid}
          >
            {loading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Criando...
              </>
            ) : (
              <>
                <IconCheck size={16} className="me-2" />
                Criar Implementação
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
      
      {/* Modal de Pagamento */}
      <SubscriptionPaymentModal
        show={showPaymentModal}
        onHide={handlePaymentCancel}
        paymentData={paymentData}
        onSuccess={handlePaymentSuccess}
      />
    </Modal>
  );
};

export default AgentImplementationModal;
